"use client";

import * as React from "react";
import DatePicker from "react-datepicker";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "./utils";
import { buttonVariants } from "./button";
import "react-datepicker/dist/react-datepicker.css";

export interface CalendarProps {
  mode?: "single" | "range";
  selected?: Date | { from: Date | undefined; to?: Date | undefined };
  onSelect?: (date: Date | { from: Date | undefined; to?: Date | undefined } | undefined) => void;
  className?: string;
  numberOfMonths?: number;
  showOutsideDays?: boolean;
  defaultMonth?: Date;
  disabled?: (date: Date) => boolean;
}

export function Calendar({
  mode = "single",
  selected,
  onSelect,
  className,
  numberOfMonths = 1,
  defaultMonth,
  disabled,
  ...props
}: CalendarProps) {
  // Handle range mode
  if (mode === "range") {
    const rangeValue = selected as { from: Date | undefined; to?: Date | undefined } | undefined;

    return (
      <div className={cn("p-3", className)}>
        <DatePicker
          selectsRange
          startDate={rangeValue?.from}
          endDate={rangeValue?.to}
          onChange={(dates) => {
            const [start, end] = dates as [Date | null, Date | null];
            onSelect?.({ from: start || undefined, to: end || undefined });
          }}
          monthsShown={numberOfMonths}
          inline
          openToDate={rangeValue?.from || defaultMonth || new Date()}
          filterDate={disabled ? (date) => !disabled(date) : undefined}
          renderCustomHeader={({
            monthDate,
            customHeaderCount,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled,
          }) => {
            // For multiple months, only show prev arrow on first month and next arrow on last month
            const showPrevButton = customHeaderCount === 0;
            const showNextButton = customHeaderCount === (numberOfMonths - 1);

            return (
              <div className="flex items-center justify-between px-2 py-2">
                <button
                  onClick={decreaseMonth}
                  disabled={prevMonthButtonDisabled}
                  type="button"
                  className={cn(
                    buttonVariants({ variant: "outline" }),
                    "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
                    !showPrevButton && "invisible"
                  )}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <div className="text-sm font-medium">
                  {monthDate.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
                </div>
                <button
                  onClick={increaseMonth}
                  disabled={nextMonthButtonDisabled}
                  type="button"
                  className={cn(
                    buttonVariants({ variant: "outline" }),
                    "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
                    !showNextButton && "invisible"
                  )}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            );
          }}
          calendarClassName="react-datepicker-custom"
          {...props}
        />
      </div>
    );
  }

  // Handle single mode
  return (
    <div className={cn("p-3", className)}>
      <DatePicker
        selected={selected as Date}
        onChange={(date) => onSelect?.(date || undefined)}
        inline
        openToDate={defaultMonth || new Date()}
        filterDate={disabled ? (date) => !disabled(date) : undefined}
        renderCustomHeader={({
          monthDate,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <div className="flex items-center justify-between px-2 py-2">
            <button
              onClick={decreaseMonth}
              disabled={prevMonthButtonDisabled}
              type="button"
              className={cn(
                buttonVariants({ variant: "outline" }),
                "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
              )}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="text-sm font-medium">
              {monthDate.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
            </div>
            <button
              onClick={increaseMonth}
              disabled={nextMonthButtonDisabled}
              type="button"
              className={cn(
                buttonVariants({ variant: "outline" }),
                "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
              )}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
        calendarClassName="react-datepicker-custom"
        {...props}
      />
    </div>
  );
}

Calendar.displayName = "Calendar";
